import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const RidgePreservation = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Ridge Preservation Fort Worth TX | Socket Preservation | Fort Worth Dental</title>
        <meta name="description" content="Ridge preservation in Fort Worth TX after tooth extraction. Maintain bone volume for future dental implants. Socket grafting experts. Call (817) 985-3337." />
        <meta name="keywords" content="ridge preservation fort worth, socket preservation fort worth tx, tooth extraction bone graft, alveolar preservation" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examines.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Ridge Preservation in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Protecting Jawbone After Tooth Extraction</p>
            <p className="text-sm mt-4 opacity-90">Socket Preservation | Bone Preservation | Implant Preparation</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Ridge Preservation */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/ridge-pres-new-1.jpg" 
                  alt="Ridge Preservation" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Ridge Preservation?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Ridge preservation, also known as socket preservation, is a procedure performed at the time of tooth extraction to minimize bone loss and preserve the shape of the jaw. When a tooth is extracted, the surrounding bone begins to deteriorate and shrink. This natural process, called resorption, can make it difficult to place a dental implant later and may affect the appearance of your smile.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Ridge preservation prevents this bone loss by filling the empty tooth socket with bone graft material immediately after extraction. This grafting material acts as a scaffold, encouraging new bone growth and maintaining the width and height of the ridge. The procedure is especially important if you plan to replace the extracted tooth with a dental implant in the future.
                </p>
              </div>
            </div>
          </section>

          {/* What Is The Procedure */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Procedure For Ridge Preservation?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Ridge preservation is typically performed immediately after tooth extraction, all in the same appointment. After your Fort Worth dentist carefully removes the tooth, the empty socket is cleaned and disinfected. The socket is then filled with bone grafting material, which may come from various sources including your own bone, donated human bone, animal-derived bone, or synthetic materials.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Once the socket is filled, a protective membrane may be placed over the graft to keep the material in place and prevent soft tissue from growing into the socket. The area is then sutured closed. Over the next several months, the grafting material integrates with your existing bone, and new bone grows to fill the socket. This healing process typically takes 3-6 months.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  During recovery, you'll need to follow post-operative instructions carefully, including eating soft foods, avoiding the extraction site when brushing, and using prescribed medications. Regular follow-up visits ensure proper healing.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/ridge-pres-new-2.jpg" 
                  alt="Ridge Preservation Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              What Are The Benefits Of Ridge Preservation?
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Maintains Bone Volume:</strong> Prevents the significant bone loss that naturally occurs after tooth extraction</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Better Implant Outcomes:</strong> Creates ideal conditions for dental implant placement with adequate bone support</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Shorter Treatment Time:</strong> Reduces or eliminates the need for additional bone grafting before implant placement</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Preserves Facial Structure:</strong> Maintains the natural contours of your jaw and prevents the sunken appearance that can occur with bone loss</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Protects Adjacent Teeth:</strong> Prevents shifting of neighboring teeth and maintains proper spacing</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Cost-Effective:</strong> Performing the procedure at the time of extraction is often less expensive than requiring a separate bone grafting surgery later</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span><strong>Better Aesthetics:</strong> Supports better cosmetic results for dental restorations and maintains gum tissue levels</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Ridge Preservation?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team specializes in advanced bone preservation techniques for optimal dental implant outcomes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Advanced Techniques</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Ridge Preservation Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about ridge preservation in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Ridge Preservation Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default RidgePreservation;